import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, ScanLine } from "lucide-react";

const QR_FG = "#082D50";
const QR_BG = "#FFFFFF";

/**
 * Full-screen-ish "Present my card" overlay: a large, high-contrast QR meant to
 * be scanned across a table at in-person events.
 */
export function PresentCardDialog({ open, onOpenChange, member, shareUrl }) {
  const [qrSize, setQrSize] = useState(300);

  useEffect(() => {
    if (!open) return;
    const calc = () => {
      const w = window.innerWidth || 360;
      setQrSize(Math.max(240, Math.min(420, Math.floor(w * 0.74))));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Card link copied");
    } catch (e) {
      toast.error("Could not copy the link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[94vw] overflow-hidden p-0 sm:max-w-md" data-testid="present-card-dialog">
        <div className="fc-hero-bg flex flex-col items-center px-5 pb-6 pt-9 text-center">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Scan to connect</p>
          <DialogTitle className="mt-1 font-serif text-2xl font-semibold text-foreground" data-testid="present-card-name">
            {member?.name || "My card"}
          </DialogTitle>
          {member?.headline ? (
            <DialogDescription className="mt-0.5 max-w-[80%] text-sm text-muted-foreground">{member.headline}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">Scan this QR code to view and save my contact card.</DialogDescription>
          )}

          <div className="mt-5 rounded-2xl border bg-white p-4 shadow-md" data-testid="present-card-qr">
            <QRCodeCanvas value={shareUrl || " "} size={qrSize} fgColor={QR_FG} bgColor={QR_BG} level="M" marginSize={2} />
          </div>

          <button
            onClick={copyLink}
            className="mt-4 inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-background/60 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
            data-testid="present-card-copy-link"
          >
            <Copy className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{shareUrl?.replace(/^https?:\/\//, "")}</span>
          </button>

          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <ScanLine className="h-3.5 w-3.5" /> Point a phone camera at the code
          </p>

          <DialogClose asChild>
            <Button variant="secondary" className="mt-5 w-full" data-testid="present-card-close">Done</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
